import {
	App,
	APP_NAME,
	APP_SETTINGS_ADAPTER,
	AppSettingsAdapter,
	deserializeUser,
	Environment,
	ENVIRONMENT,
	getUserFullName,
	mapUserFormat,
	NumberFormat,
	Role,
	TimeFormat,
	User,
	UserDateFormat,
	UserFormat,
} from '@agilox/common';
import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { UserAppSettingsClient } from './user-app-settings.client';

@Injectable({
	providedIn: 'root',
})
export class UserStoreService<T extends { id: string } = any> {
	private httpClient = inject(HttpClient);
	private translate = inject(TranslateService);
	private environment = inject<Environment>(ENVIRONMENT);
	private appName = inject<App>(APP_NAME, { optional: true });
	private adapter = inject<AppSettingsAdapter<T>>(APP_SETTINGS_ADAPTER, { optional: true });
	private userAppSettingsClient = inject(UserAppSettingsClient);

	currentUser = computed(() => this.user());
	currentAppSettings = computed(() => this.appSettings());

	role = computed(() => this.user()?.role ?? '');

	isInternalUser = computed(
		() =>
			this.role() === Role.service || this.role() === Role.support || this.role() === Role.superuser
	);

	userFullName = computed(() => getUserFullName(this.user()));

	user = signal<User | undefined>(undefined);
	appSettings = signal<T | undefined>(undefined);

	userFormat = signal<UserFormat>({
		date: UserDateFormat.DMY_DOT,
		time: TimeFormat.TWENTY_FOUR_HOUR,
		number: NumberFormat.DOT_THOUSAND_COMMA_DECIMAL,
	});

	role$ = toObservable(this.role);

	loadCurrent(): Observable<User> {
		return this.httpClient
			.get<any>(`${this.environment.serverNew}/user/v1/users/current?include=settings,grants`)
			.pipe(
				switchMap((user: any) => {
					return deserializeUser(user);
				}),
				tap((user: User) => {
					this.setUser(user);
				}),
				switchMap((user: User) => {
					if (this.appName && this.adapter) {
						return this.loadAppSettings(user).pipe(switchMap(() => of(user)));
					}
					return of(user);
				}),
				catchError((error) => {
					return throwError(() => error);
				})
			);
	}

	private loadAppSettings(user: User): Observable<void> {
		if (!this.appName || !this.adapter || !user.id) {
			return of(undefined);
		}

		return this.userAppSettingsClient.getCurrent(this.appName, user.id).pipe(
			switchMap((response) => {
				return response
					? this.handleExistingSettings(response)
					: this.createDefaultSettings(user.id!);
			}),
			catchError(() => of(undefined))
		);
	}

	private handleExistingSettings(response: { id: string; settings: any }): Observable<void> {
		try {
			const parsed = this.adapter!.parse(response.id, response.settings);
			this.appSettings.set(parsed);
			return of(undefined);
		} catch (error) {
			return this.updateWithDefaults(response.id);
		}
	}

	private updateWithDefaults(settingsId: string): Observable<void> {
		const defaults = this.adapter!.defaults(settingsId);
		this.appSettings.set(defaults);

		return this.userAppSettingsClient.update(settingsId, this.adapter!.toPayload(defaults)).pipe(
			switchMap(() => of(undefined)),
			catchError(() => of(undefined))
		);
	}

	private createDefaultSettings(userId: string): Observable<void> {
		const defaultPayload = this.adapter!.toPayload(this.adapter!.defaults(''));

		return this.userAppSettingsClient.create(this.appName!, userId, defaultPayload).pipe(
			tap((created) => {
				const defaults = this.adapter!.defaults(created.id);
				this.appSettings.set(defaults);
			}),
			switchMap(() => of(undefined)),
			catchError(() => of(undefined))
		);
	}

	updateAppSettings(settings: T): Observable<void> {
		if (!this.adapter || !settings?.id) {
			return throwError(() => new Error('App settings not initialized'));
		}

		const payload = this.adapter.toPayload(settings);
		return this.userAppSettingsClient.update(settings.id, payload).pipe(
			tap(() => {
				this.appSettings.set(settings);
			}),
			switchMap(() => of(undefined))
		);
	}

	setUser(user: User): void {
		this.user.set(user);

		if (user.format) {
			this.userFormat.set(mapUserFormat(user.format));
		}

		if (user.lang) {
			this.setLanguage(user.lang);
		}
	}

	updateUserFormat(userFormat: UserFormat): void {
		this.userFormat.set(mapUserFormat(userFormat));
	}

	setLanguage(language?: string): void {
		if (language) {
			this.translate.use(language.toLowerCase());
		} else {
			const userLang = this.user()?.lang || 'en';
			this.translate.setDefaultLang(userLang);
			this.translate.use(userLang);
		}
	}

	clearUser(): void {
		this.user.set(undefined);
		this.appSettings.set(undefined);
		this.userFormat.set({
			date: UserDateFormat.DMY_DOT,
			time: TimeFormat.TWENTY_FOUR_HOUR,
			number: NumberFormat.DOT_THOUSAND_COMMA_DECIMAL,
		});
	}
}
